import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Download, 
  ArrowLeft, 
  UserSquare2, 
  Printer, 
  AlertCircle, 
  CheckCircle2, 
  Loader2,
  Search,
  Users,
  Settings2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { stationeryAPI, employeeAPI, companyAPI } from '../../lib/api';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

const MM_TO_PT = 2.83465; // jsPDF uses points (72dpi), 1mm = 2.83465 points

const SHAPE_CLIP_PATHS = {
  rectangle: 'none',
  circle: 'circle(50% at 50% 50%)',
  triangle: 'polygon(50% 0%, 0% 100%, 100% 100%)',
  diamond: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  star: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
  hexagon: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
};

const StationeryGenerator = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [template, setTemplate] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tplRes, empRes] = await Promise.all([
        stationeryAPI.getTemplate(id),
        employeeAPI.getEmployees({ page_size: 1000 })
      ]);
      setTemplate(tplRes.data);
      setEmployees(empRes.data.employees);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(emp => 
    `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
    emp.employee_code.toLowerCase().includes(search.toLowerCase())
  );

  const getElementValue = (element, employee) => {
    if (element.type !== 'text') return element.content;
    
    let text = element.content;
    const data = {
      '{firstName}': employee.first_name,
      '{lastName}': employee.last_name,
      '{employeeCode}': employee.employee_code,
      '{department}': employee.department || 'N/A',
      '{designation}': employee.designation || 'N/A',
      '{phone}': employee.primary_phone || employee.phone || 'N/A',
      '{email}': employee.email,
      '{bloodGroup}': employee.blood_group || 'N/A',
      '{doj}': employee.date_of_joining,
      '{companyName}': 'Company Name' 
    };

    Object.keys(data).forEach(key => {
      text = text.replace(new RegExp(key, 'g'), data[key]);
    });
    
    return text;
  };

  const generatePDF = async () => {
    if (!selectedEmployee || !template) return;

    if (!selectedEmployee.profile_picture) {
      setErrorModal({
        open: true,
        message: `Employee ${selectedEmployee.first_name} ${selectedEmployee.last_name} does not have a profile picture. Please upload one in their profile before generating high-quality stationery.`
      });
      return;
    }

    try {
      setGenerating(true);
      const { type, design_json } = template;
      const cardSize = type === 'ID_CARD' ? [54, 86] : [89, 51]; 
      
      const pdf = new jsPDF({
        unit: 'mm',
        format: cardSize,
        orientation: type === 'ID_CARD' ? 'portrait' : 'landscape'
      });

      const drawSide = async (side) => {
        const elements = design_json.elements[side] || [];
        const background = design_json.backgrounds[side];
        const backgroundImage = design_json.backgrounds[`${side}Image`];

        if (background) {
          pdf.setFillColor(background);
          pdf.rect(0, 0, cardSize[0], cardSize[1], 'F');
        }

        if (backgroundImage) {
          try {
            await new Promise((resolve) => {
              const img = new Image();
              img.crossOrigin = "Anonymous";
              img.onload = () => {
                pdf.addImage(img, 'JPEG', 0, 0, cardSize[0], cardSize[1]);
                resolve();
              };
              img.onerror = resolve; 
              img.src = backgroundImage;
            });
          } catch (e) {
            console.error("BG image fail", e);
          }
        }

        for (const el of elements.sort((a,b) => a.zIndex - b.zIndex)) {
          if (el.type === 'text') {
            const val = getElementValue(el, selectedEmployee);
            pdf.setTextColor(el.color);
            pdf.setFontSize(el.fontSize);
            pdf.setFont(undefined, el.fontWeight === 'bold' || el.fontWeight === '900' ? 'bold' : 'normal');
            
            const align = el.textAlign === 'center' ? 'center' : (el.textAlign === 'right' ? 'right' : 'left');
            let x = el.x;
            if (align === 'center') x += el.width / 2;
            if (align === 'right') x += el.width;

            pdf.text(val, x, el.y + (el.fontSize / 3.5), { align }); 
          } else if (el.type === 'shape') {
             pdf.setFillColor(el.color);
             const { width: w, height: h, x, y, shapeType, borderRadius: br } = el;
             
             if (shapeType === 'circle') {
                pdf.ellipse(x + w/2, y + h/2, w/2, h/2, 'F');
             } else if (shapeType === 'triangle') {
                pdf.lines([[w/2, h], [-w, 0]], x + w/2, y, [1, 1], 'F', true);
             } else if (shapeType === 'diamond') {
                pdf.lines([[w/2, h/2], [-w/2, h/2], [-w/2, -h/2]], x + w/2, y, [1, 1], 'F', true);
             } else if (shapeType === 'star') {
                // Approximate star with lines
                const pts = [
                   [w*0.11, h*0.35], [w*0.37, 0], [w*0.3, -h*0.22], [w*0.3, h*0.22], [w*0.11, h*0.34],
                   [-w*0.29, -h*0.21], [-w*0.29, h*0.21], [-w*0.3, -h*0.22], [-w*0.3, h*0.22]
                ];
                pdf.lines(pts, x + w*0.5, y, [1, 1], 'F', true);
             } else if (shapeType === 'hexagon') {
                const pts = [[w*0.5, 0], [w*0.25, h*0.5], [-w*0.25, h*0.5], [-w*0.5, 0], [-w*0.25, -h*0.5]];
                pdf.lines(pts, x + w*0.25, y, [1, 1], 'F', true);
             } else {
                if (br > 0) {
                   pdf.roundedRect(x, y, w, h, br, br, 'F');
                } else {
                   pdf.rect(x, y, w, h, 'F');
                }
             }
          } else if (el.type === 'photo') {
            const photoUrl = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}${selectedEmployee.profile_picture}`;
            try {
              await new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                  pdf.addImage(img, 'JPEG', el.x, el.y, el.width, el.height);
                  resolve();
                };
                img.onerror = resolve;
                img.src = photoUrl;
              });
            } catch (e) {}
          } else if (el.type === 'logo') {
            const compRes = await companyAPI.getCurrent();
            const logoUrl = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}${compRes.data.logo}`;
            try {
              await new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                  pdf.addImage(img, 'PNG', el.x, el.y, el.width, el.height);
                  resolve();
                };
                img.onerror = resolve;
                img.src = logoUrl;
              });
            } catch (e) {}
          }
        }
      };

      await drawSide('front');
      
      const hasBack = design_json.elements.back.length > 0 || (design_json.backgrounds.back && design_json.backgrounds.back !== '#f8f9fa') || design_json.backgrounds.backImage;
      if (hasBack) {
        pdf.addPage();
        await drawSide('back');
      }

      pdf.save(`${template.name}_${selectedEmployee.employee_code}.pdf`);
      toast.success('Vector PDF generated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF export');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
     return (
       <div className="h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-900">
         <Loader2 className="animate-spin text-primary mb-5" size={56} strokeWidth={1} />
         <div className="text-center">
            <h3 className="text-lg font-black tracking-tighter uppercase mb-1">Pre-flight Engine Check</h3>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-widest animate-pulse">Loading vectors and personnel data</p>
         </div>
       </div>
     );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-8 animate-in fade-in duration-500">
      <div className="max-w-7xl mx-auto space-y-8">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <Button variant="ghost" size="icon" onClick={() => navigate('/stationery')} className="h-12 w-12 rounded-xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:bg-slate-50 transition-all">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-none">Generate {template?.name}</h1>
              <p className="text-slate-500 font-medium text-sm mt-1.5 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  Asset Generation Hub • {template?.type.replace('_', ' ')}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
             <Button variant="outline" className="font-bold gap-2 h-11 border-slate-200 bg-white" onClick={() => navigate(`/stationery/designer/${template?.type.toLowerCase()}/${template?.id}`)}>
               <Settings2 size={18} /> Edit Core Template
             </Button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left: Employee Selection */}
          <Card className="lg:col-span-4 bg-white border-slate-200 shadow-xl overflow-hidden flex flex-col max-h-[750px] sticky top-8">
            <CardHeader className="p-5 border-b border-slate-100 bg-slate-50/50">
               <div className="relative">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                 <Input 
                   placeholder="Search personnel by name or ID..." 
                   value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   className="pl-11 h-12 bg-white border-slate-200 focus:ring-2 focus:ring-primary/20 text-sm font-medium"
                 />
               </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-auto bg-white custom-scrollbar">
              <div className="divide-y divide-slate-50">
                {filteredEmployees.map(emp => (
                  <div 
                    key={emp.id} 
                    className={cn(
                      "p-4 flex items-center gap-4 cursor-pointer transition-all duration-200 group relative",
                      selectedEmployee?.id === emp.id ? "bg-primary/5 translate-x-1" : "hover:bg-slate-50"
                    )}
                    onClick={() => setSelectedEmployee(emp)}
                  >
                     <div className="h-12 w-12 rounded-2xl bg-white shrink-0 border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm group-hover:shadow-md transition-shadow">
                       {emp.profile_picture ? (
                         <img src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}${emp.profile_picture}`} className="h-full w-full object-cover" />
                       ) : (
                         <div className="uppercase text-slate-300 font-black text-xl">{emp.first_name[0]}{emp.last_name[0]}</div>
                       )}
                     </div>
                     <div className="flex-1 overflow-hidden">
                       <p className={cn(
                           "text-sm font-bold truncate transition-colors uppercase tracking-tight",
                           selectedEmployee?.id === emp.id ? "text-primary" : "text-slate-900"
                        )}>
                         {emp.first_name} {emp.last_name}
                       </p>
                       <p className="text-[10px] text-slate-500 font-bold uppercase font-mono mt-0.5 tracking-tighter opacity-80">{emp.employee_code} • {emp.designation || 'Staff'}</p>
                     </div>
                     {selectedEmployee?.id === emp.id && (
                         <div className="h-2 w-2 rounded-full bg-primary animate-pulse shadow-[0_0_8px_rgba(var(--primary),0.5)]" />
                     )}
                     
                     {/* Hover accent */}
                     <div className={cn(
                         "absolute left-0 top-0 bottom-0 w-1 bg-primary scale-y-0 transition-transform origin-top",
                         selectedEmployee?.id === emp.id ? "scale-y-100" : ""
                     )} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Right: Preview & Action */}
          <div className="lg:col-span-8 space-y-6">
             <Card className="bg-slate-200/50 border-slate-200 relative overflow-hidden flex flex-col items-center justify-center p-16 min-h-[500px] shadow-inner rounded-3xl border-dashed border-2">
                {!selectedEmployee ? (
                  <div className="text-center space-y-5">
                    <div className="h-20 w-20 bg-white rounded-3xl flex items-center justify-center mx-auto shadow-sm border border-slate-200">
                      <Users size={32} strokeWidth={1} className="text-slate-300" />
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="font-black text-lg text-slate-400 uppercase tracking-tighter">No Talent Selected</h3>
                      <p className="text-xs text-slate-500 font-medium max-w-xs mx-auto leading-relaxed italic">Select an employee from the panel on the left to initialize the vector preview engine.</p>
                    </div>
                  </div>
                ) : (
                  <div className="animate-in fade-in zoom-in-105 duration-500 flex flex-col items-center">
                     <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-8 bg-white px-4 py-1.5 rounded-full border border-slate-200 shadow-sm">
                         Real-time Render Pipeline
                     </span>
                     
                     <div className="relative group">
                        <div className="absolute -inset-4 bg-primary/5 rounded-[2.5rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                        <div className="relative shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] border-8 border-white p-0 bg-white rounded-xl overflow-hidden ring-1 ring-slate-100">
                            <div 
                                className="bg-white"
                                style={{
                                    width: template.type === 'ID_CARD' ? '216px' : '356px',
                                    height: template.type === 'ID_CARD' ? '344px' : '204px',
                                    position: 'relative',
                                    backgroundColor: template.design_json.backgrounds.front,
                                    backgroundImage: template.design_json.backgrounds.frontImage ? `url(${template.design_json.backgrounds.frontImage})` : 'none',
                                    backgroundSize: 'cover'
                                }}
                            >
                                {template.design_json.elements.front.map(el => (
                                    <div 
                                        key={el.id}
                                        style={{
                                            position: 'absolute',
                                            left: `${el.x * 4}px`, 
                                            top: `${el.y * 4}px`,
                                            width: `${el.width * 4}px`,
                                            height: `${el.height * 4}px`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: el.textAlign === 'center' ? 'center' : (el.textAlign === 'right' ? 'flex-end' : 'flex-start'),
                                            fontSize: `${el.fontSize * 1.5}px`, 
                                            fontWeight: el.fontWeight,
                                            color: el.color,
                                            opacity: el.opacity,
                                            backgroundColor: (el.type === 'shape' && el.shapeType !== 'custom') ? el.color : 'transparent',
                                            borderRadius: (el.type === 'shape' && el.shapeType === 'circle') ? '100%' : (el.borderRadius ? `${el.borderRadius * 4}px` : 0),
                                            clipPath: el.type === 'shape' ? SHAPE_CLIP_PATHS[el.shapeType || 'rectangle'] : 'none',
                                        }}
                                    >
                                        {el.type === 'text' && getElementValue(el, selectedEmployee)}
                                        {el.type === 'photo' && (
                                            <div className="w-full h-full overflow-hidden" style={{ borderRadius: el.borderRadius ? `${el.borderRadius * 4}px` : 0 }}>
                                            {selectedEmployee.profile_picture ? (
                                                <img src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}${selectedEmployee.profile_picture}`} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-slate-100 flex items-center justify-center text-[8px] font-bold text-slate-300 uppercase">NO PHOTO</div>
                                            )}
                                            </div>
                                        )}
                                        {el.type === 'shape' && el.shapeType === 'custom' && (
                                            <svg width="100%" height="100%" viewBox="0 0 10 10" preserveAspectRatio="none">
                                                <path d={el.content || 'M 0 0 L 10 0 L 10 10 L 0 10 Z'} fill={el.color} />
                                            </svg>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                     </div>
                  </div>
                )}
             </Card>

             <div className={cn("transition-all duration-500 transform", selectedEmployee ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none")}>
                <Button 
                    className="w-full h-16 bg-slate-900 hover:bg-slate-800 text-white text-lg font-black uppercase tracking-widest gap-4 shadow-2xl disabled:opacity-50 transition-all active:scale-[0.98]"
                    disabled={!selectedEmployee || generating}
                    onClick={generatePDF}
                >
                    {generating ? (
                    <>
                        <Loader2 className="animate-spin" size={24} /> Compiling High-Resolution Export
                    </>
                    ) : (
                    <>
                        <Download size={24} strokeWidth={3} /> Generate Production PDF
                    </>
                    )}
                </Button>
             </div>

             <div className="grid grid-cols-3 gap-6 pt-4">
                <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm text-center group transition-all hover:border-primary/30">
                   <p className="text-[10px] text-slate-400 uppercase font-black mb-1 group-hover:text-primary transition-colors">Precision</p>
                   <p className="text-sm font-black text-slate-900 font-mono">1:1 SCALE</p>
                </div>
                <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm text-center group transition-all hover:border-primary/30">
                   <p className="text-[10px] text-slate-400 uppercase font-black mb-1 group-hover:text-primary transition-colors">Output Type</p>
                   <p className="text-sm font-black text-slate-900 font-mono">VECTOR PDF</p>
                </div>
                <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm text-center group transition-all hover:border-primary/30">
                   <p className="text-[10px] text-slate-400 uppercase font-black mb-1 group-hover:text-primary transition-colors">Engine</p>
                   <p className="text-sm font-black text-slate-900 font-mono">PAGAHR JS</p>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <Dialog open={errorModal.open} onOpenChange={(open) => setErrorModal({ ...errorModal, open })}>
        <DialogContent className="bg-white p-0 overflow-hidden border-none shadow-2xl max-w-md">
          <div className="p-8 pb-4">
            <div className="h-16 w-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6 border border-red-100 mx-auto">
              <AlertCircle size={32} className="text-red-600" />
            </div>
            <DialogHeader className="space-y-2">
                <DialogTitle className="text-center text-2xl font-black text-slate-900 uppercase tracking-tight">Requirement Missing</DialogTitle>
                <DialogDescription className="text-center text-slate-500 font-medium leading-relaxed px-4">
                {errorModal.message}
                </DialogDescription>
            </DialogHeader>
          </div>
          <DialogFooter className="p-6 bg-slate-50 border-t border-slate-100 sm:flex-col gap-3">
            <Button 
              onClick={() => {
                setErrorModal({ ...errorModal, open: false });
                if (selectedEmployee) navigate(`/employees/${selectedEmployee.id}`);
              }}
              className="w-full h-12 text-sm font-black uppercase tracking-wider"
            >
              Update Employee Profile
            </Button>
            <Button 
              variant="ghost"
              onClick={() => setErrorModal({ ...errorModal, open: false })}
              className="w-full text-slate-400 hover:text-slate-600 font-bold text-xs uppercase"
            >
                Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StationeryGenerator;
